import React, { useState, useEffect } from 'react';
import { useData } from '../../contexts/DataContext';
import api from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { Building2, Plus, Edit, Trash2, MapPin, Phone, Mail, Globe, Factory } from 'lucide-react';
import { DataTable } from '../../components/ui/data-table';

const PlantsPage = () => {
  const { fetchPlants } = useData();
  const [plants, setPlants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editPlant, setEditPlant] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    plant_type: '',
    address: '',
    city: '',
    country: 'Romania',
    phone: '',
    email: '',
    website: '',
    description: '',
    is_active: true
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await api.get('/plants');
      setPlants(response.data);
    } catch (error) {
      console.error('Error loading plants:', error);
      toast.error('Eroare la incarcarea datelor');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editPlant) {
        await api.put(`/plants/${editPlant.id}`, formData);
        toast.success('Sediu actualizat!');
      } else {
        await api.post('/plants', formData);
        toast.success('Sediu creat!');
      }
      setShowAddDialog(false);
      setEditPlant(null);
      resetForm();
      loadData();
      fetchPlants(); // Update global context
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la salvare');
    }
  };

  const handleEdit = (plant) => {
    setEditPlant(plant);
    setFormData({
      name: plant.name || '',
      plant_type: plant.plant_type || '',
      address: plant.address || '',
      city: plant.city || '',
      country: plant.country || 'Romania',
      phone: plant.phone || '',
      email: plant.email || '',
      website: plant.website || '',
      description: plant.description || '',
      is_active: plant.is_active !== false
    });
    setShowAddDialog(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sigur doriti sa stergeti acest sediu? Toate locatiile asociate vor fi afectate.')) return;
    try {
      await api.delete(`/plants/${id}`);
      toast.success('Sediu sters!');
      loadData();
      fetchPlants();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la stergere');
    }
  };

  const toggleActive = async (plant) => {
    try {
      await api.put(`/plants/${plant.id}`, { ...plant, is_active: !plant.is_active });
      toast.success(`Sediu ${!plant.is_active ? 'activat' : 'dezactivat'}!`);
      loadData();
      fetchPlants();
    } catch (error) {
      toast.error('Eroare la actualizare');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      plant_type: '',
      address: '',
      city: '',
      country: 'Romania',
      phone: '',
      email: '',
      website: '',
      description: '',
      is_active: true
    });
  };

  const getPlantTypeLabel = (type) => {
    const types = {
      'PVC': 'PVC',
      'Aluminium': 'Aluminiu',
      'Glass': 'Sticla',
      'Wood': 'Lemn',
      'Steel': 'Otel',
      'Production': 'Productie',
      'Warehouse': 'Depozit',
      'Office': 'Birou'
    };
    return types[type] || type || 'N/A';
  };

  const getPlantIcon = (type) => {
    if (type === 'Warehouse') return '🏢';
    if (type === 'Office') return '🏬';
    return '🏭';
  };

  // Prepare data for DataTable
  const plantsData = plants.map(plant => ({
    ...plant,
    type_display: getPlantTypeLabel(plant.plant_type),
    status_display: plant.is_active !== false ? 'Activa' : 'Inactiva',
    full_address: [plant.address, plant.city, plant.country].filter(Boolean).join(', ') || '-'
  }));

  // Columns for DataTable
  const tableColumns = [
    {
      key: 'name',
      label: 'Nume',
      sortable: true,
      filterable: true,
      render: (value, row) => (
        <div className="flex items-center gap-2">
          <span className="text-lg">{getPlantIcon(row.plant_type)}</span>
          <div>
            <span className="font-medium">{value}</span>
            {row.description && (
              <p className="text-xs text-slate-500 truncate max-w-[200px]">{row.description}</p>
            )}
          </div>
        </div>
      )
    },
    {
      key: 'type_display',
      label: 'Tip',
      sortable: true,
      filterable: true,
      width: 'w-32',
      render: (value) => (
        <Badge variant="outline">{value}</Badge>
      )
    },
    {
      key: 'full_address',
      label: 'Adresa',
      sortable: false,
      filterable: true,
      render: (value) => (
        <span className="text-slate-600 text-sm">{value}</span>
      )
    },
    {
      key: 'phone',
      label: 'Contact',
      sortable: false,
      filterable: false,
      render: (value, row) => (
        <div className="text-sm">
          {value && <div className="flex items-center gap-1"><Phone className="w-3 h-3" /> {value}</div>}
          {row.email && <div className="flex items-center gap-1 text-slate-500"><Mail className="w-3 h-3" /> {row.email}</div>}
        </div>
      )
    },
    {
      key: 'status_display',
      label: 'Status',
      sortable: true,
      filterable: true,
      width: 'w-24',
      render: (value, row) => (
        <Badge className={row.is_active !== false ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-700'}>
          {value}
        </Badge>
      )
    },
    {
      key: 'actions',
      label: 'Actiuni',
      sortable: false,
      filterable: false,
      width: 'w-32',
      render: (_, row) => (
        <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleEdit(row)}
            data-testid={`edit-plant-${row.id}`}
          >
            <Edit className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => toggleActive(row)}
            className={row.is_active !== false ? 'text-orange-600' : 'text-green-600'}
            title={row.is_active !== false ? 'Dezactiveaza' : 'Activeaza'}
          >
            {row.is_active !== false ? '⏸' : '▶'}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleDelete(row.id)}
            className="text-red-600 hover:text-red-700"
            data-testid={`delete-plant-${row.id}`}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      )
    }
  ];

  return (
    <div className="space-y-6" data-testid="plants-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Sedii</h1>
          <p className="text-slate-600 mt-1">Gestioneaza sediile si punctele de lucru ({plants.length} total)</p>
        </div>
        <Dialog open={showAddDialog} onOpenChange={(open) => {
          setShowAddDialog(open);
          if (!open) { setEditPlant(null); resetForm(); }
        }}>
          <DialogTrigger asChild>
            <Button className="btn-primary h-12" data-testid="add-plant-btn">
              <Plus className="w-5 h-5 mr-2" />
              SEDIU NOU
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editPlant ? 'Editeaza Sediu' : 'Adauga Sediu'}</DialogTitle>
              <DialogDescription>
                Completati informatiile pentru sediu
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nume *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="h-11"
                    placeholder="ex: Sediu Leonida"
                    data-testid="plant-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Tip sediu</Label>
                  <Select
                    value={formData.plant_type}
                    onValueChange={(value) => setFormData({ ...formData, plant_type: value })}
                  >
                    <SelectTrigger className="h-11">
                      <SelectValue placeholder="Selecteaza tipul" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PVC">🏭 PVC</SelectItem>
                      <SelectItem value="Aluminium">🏭 Aluminiu</SelectItem>
                      <SelectItem value="Glass">🏭 Sticla</SelectItem>
                      <SelectItem value="Wood">🏭 Lemn</SelectItem>
                      <SelectItem value="Steel">🏭 Otel</SelectItem>
                      <SelectItem value="Production">🏭 Productie</SelectItem>
                      <SelectItem value="Warehouse">🏢 Depozit</SelectItem>
                      <SelectItem value="Office">🏬 Birou</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Descriere</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={2}
                  placeholder="Descriere optionala"
                />
              </div>

              {/* Adresa */}
              <div className="border rounded-lg p-4 space-y-4 bg-slate-50">
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-[#0052CC]" />
                  <Label className="font-medium">Adresa</Label>
                </div>
                <div className="space-y-2">
                  <Input
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    className="h-10"
                    placeholder="Strada, numar"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm">Oras</Label>
                    <Input
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      className="h-10"
                      placeholder="ex: Bucuresti"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm">Tara</Label>
                    <Input
                      value={formData.country}
                      onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                      className="h-10"
                      placeholder="Romania"
                    />
                  </div>
                </div>
              </div>

              {/* Contact */}
              <div className="border rounded-lg p-4 space-y-4 bg-slate-50">
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-[#0052CC]" />
                  <Label className="font-medium">Contact</Label>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm">Telefon</Label>
                    <Input
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="h-10"
                      placeholder="+40 xxx xxx xxx"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm">Email</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="h-10"
                      placeholder="contact@sediu.ro"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Website</Label>
                  <Input
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    className="h-10"
                    placeholder="https://www.sediu.ro"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <Label className="font-medium">Status activ</Label>
                  <p className="text-sm text-slate-500">Fabricile inactive nu apar in selectii</p>
                </div>
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>
                  Anuleaza
                </Button>
                <Button type="submit" className="btn-primary" data-testid="save-plant-btn">
                  {editPlant ? 'ACTUALIZEAZA' : 'SALVEAZA'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="card-industrial">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{plants.length}</p>
                <p className="text-sm text-slate-500">Total Sedii</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-industrial">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <Factory className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{plants.filter(p => p.is_active !== false).length}</p>
                <p className="text-sm text-slate-500">Active</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-industrial">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <span className="text-lg">🏭</span>
              </div>
              <div>
                <p className="text-2xl font-bold">{plants.filter(p => p.plant_type === 'Production' || !p.plant_type).length}</p>
                <p className="text-sm text-slate-500">Productie</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-industrial">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                <span className="text-lg">🏢</span>
              </div>
              <div>
                <p className="text-2xl font-bold">{plants.filter(p => p.plant_type === 'Warehouse').length}</p>
                <p className="text-sm text-slate-500">Depozite</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* DataTable */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0052CC]"></div>
        </div>
      ) : (
        <DataTable
          data={plantsData}
          columns={tableColumns}
          pageSize={10}
          searchable={true}
          exportable={true}
          printable={true}
          selectable={false}
          onRowClick={handleEdit}
          emptyMessage="Nu exista sedii inregistrate"
        />
      )}
    </div>
  );
};

export default PlantsPage;
